import { formatMoney, labelOf, ROASTS } from '@c214/shared';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { PackArt } from '../components/product/PackArt';
import { Button, buttonVariants } from '../components/ui/button';
import { Chip } from '../components/ui/chip';
import { type ProductStatus, useAdminProducts } from '../hooks/useAdminProducts';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/utils';
import { useAuthModal } from '../providers';

export function AdminProductsPage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const authModal = useAuthModal();
  const [status, setStatus] = useState<ProductStatus>('active');
  const { data: products = [], isLoading, isError } = useAdminProducts(status);

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб керувати товарами.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="section-pad">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="m-0 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
          Товари
        </h1>
        <Link
          to="/admin/products/new"
          className={cn(buttonVariants({ variant: 'accent', size: 'md' }), 'no-underline')}
        >
          + НОВИЙ ТОВАР
        </Link>
      </div>

      <div className="mb-6 flex gap-2">
        <Chip active={status === 'active'} onClick={() => setStatus('active')}>
          Активні
        </Chip>
        <Chip active={status === 'archived'} onClick={() => setStatus('archived')}>
          Архів
        </Chip>
      </div>

      {isLoading ? (
        <p className="mono-label">ЗАВАНТАЖЕННЯ…</p>
      ) : isError ? (
        <p className="text-[13px] text-warn">Не вдалося завантажити товари.</p>
      ) : products.length === 0 ? (
        <p className="text-[13px] text-muted">
          {status === 'active' ? 'Активних товарів немає.' : 'Архів порожній.'}
        </p>
      ) : (
        <div className="divide-y divide-line-soft border-y border-line-soft">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/admin/products/${product.id}`}
              className="flex flex-wrap items-center gap-4 py-4 text-ink no-underline hover:bg-surface"
            >
              <PackArt variant="mini" name={product.name} className="h-[66px] w-[52px] shrink-0" />
              <div className="min-w-40 flex-[2]">
                <p className="m-0 text-[17px] font-bold tracking-[-0.02em]">{product.name}</p>
                <p className="mono-label m-0 mt-1">
                  {labelOf(ROASTS, product.roast)} · {product.region}
                </p>
              </div>
              {product.inSubscription && <span className="mono-label text-accent">У ПІДПИСЦІ</span>}
              <span className="ml-auto font-mono text-[15px]">{formatMoney(product.price)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
